import React, { useCallback, useState } from 'react';
import { Handle, NodeToolbar, Position, useReactFlow, type NodeProps } from '@xyflow/react';
import { useProjectStore } from '../../store/projectStore';
import { apiClient } from '../../api/client';
import type { ExportJob } from '../../api/types';
import { useJobPolling } from '../../panels/useJobPolling';

/**
 * Container Node Data Schema
 *
 * Defines the data structure stored in a container node. This node represents
 * an entire child graph and serves as the entry point for nesting.
 *
 * @typedef {Object} ContainerNodeData
 * @property {string} label - Display name of the container (shown in node chrome)
 * @property {string} [description] - Optional description/documentation
 * @property {string} [color] - Hex color for the status bar
 * @property {number} [outputs] - Number of output pins (default 1)
 * @property {string} [childGraphUuid] - UUID of the child graph (null until created)
 * @property {number} [childNodeCount] - Cached count of nodes in child graph
 */
export interface ContainerNodeData {
    label: string;
    description?: string;
    color?: string;
    outputs?: number;
    childGraphUuid?: string;
    childNodeCount?: number;
    [key: string]: unknown;
}

/**
 * Container Node Component
 *
 * Displays a container node that represents a nested child graph. The container
 * acts as a visual and logical boundary between the parent graph and the nested
 * content within it.
 *
 * **Visual Features:**
 * - Dashed border indicating it contains other nodes
 * - Color bar at the top (customizable)
 * - Badge showing child node count
 * - Open button in toolbar for entering the child graph
 * - Double-click to submerge (open the child graph)
 *
 * **Interaction:**
 * - Single click: select the container
 * - Double-click: open/submerge into child graph
 * - Toolbar "Open" button: open child graph
 * - Input handle (left): receives connections from parent graph
 * - Output handles (right): one per output pin, defined by `data.outputs`
 *
 * **Child Graph Lifecycle:**
 * 1. Container created (child graph NOT created yet)
 * 2. User double-clicks or presses Open
 * 3. Child graph created via API (seeded with Entry/Exit nodes)
 * 4. Canvas switches to show child graph
 * 5. Breadcrumbs updated to show nesting path
 *
 * **Pin Pass-through:**
 * Entry nodes (one per input) receive connections from the parent through this
 * container's input handle. Exit nodes (one per output) send connections back
 * to the parent through output handles.
 *
 * @component
 * @param {NodeProps} props - React Flow node properties
 * @param {ContainerNodeData} props.data - Container configuration
 * @param {string} props.id - Unique node identifier
 * @param {boolean} props.selected - Whether node is currently selected
 * @param {boolean} props.isConnecting - Whether a connection is being drawn
 *
 * @example
 * // A container representing a dialogue sequence
 * const dialogueContainer = {
 *   id: 'npc_talk',
 *   type: 'container',
 *   position: { x: 100, y: 100 },
 *   data: {
 *     label: 'NPC Dialogue',
 *     outputs: 3,
 *     color: '#0d6efd',
 *   },
 * };
 */
export const ContainerNode: React.FC<NodeProps<ContainerNodeData>> = ({
    data,
    selected,
    id,
}) => {
    const nodeData = data as ContainerNodeData;
    const openGraph = useProjectStore((state) => state.openGraph);
    const currentGraph = useProjectStore((state) => state.currentGraph);
    const { setNodes } = useReactFlow();
    const [exportJob, setExportJob] = useState<ExportJob | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    const [isCreatingChildGraph, setIsCreatingChildGraph] = useState(false);
    const [openError, setOpenError] = useState<string | null>(null);

    const refetchExport = useCallback(() => apiClient.getExport(exportJob!.uuid), [exportJob]);
    useJobPolling(exportJob, refetchExport, setExportJob);

    /**
     * Export this container's subtree as a `.dgpack` bundle (doc 09 §2).
     * Node `id` is the node's uuid (React Flow node ids are set to the
     * node uuid throughout this app — see projectStore.ts openGraph).
     */
    const handleExportBundle = useCallback(async () => {
        setExportError(null);
        setExportJob(null);
        try {
            setExportJob(await apiClient.exportNodeBundle(id));
        } catch (e) {
            setExportError((e as Error).message);
        }
    }, [id]);

    /**
     * Handle opening the child graph
     *
     * Triggers navigation to the child graph. If the child graph doesn't
     * exist yet (every container's state until first opened), it's created
     * lazily via `POST /nodes/{node}/child-graph` (seeded server-side with
     * Entry/Exit nodes matching the container's pin configuration), the
     * new graph's uuid is written back onto this node so a second
     * double-click doesn't recreate it, then we navigate into it.
     *
     * @async
     */
    const handleOpenChild = useCallback(async () => {
        if (isCreatingChildGraph) return; // guard against double-click + toolbar click racing each other

        setOpenError(null);

        let childGraphUuid = nodeData.childGraphUuid;

        if (!childGraphUuid) {
            setIsCreatingChildGraph(true);
            try {
                const childGraph = await apiClient.createChildGraph(id);
                childGraphUuid = childGraph.uuid;
                setNodes((nodes) =>
                    nodes.map((n) =>
                        n.id === id ? { ...n, data: { ...n.data, childGraphUuid } } : n
                    )
                );
            } catch (e) {
                setOpenError((e as Error).message);
                return;
            } finally {
                setIsCreatingChildGraph(false);
            }
        }

        await openGraph(childGraphUuid);
    }, [id, nodeData.childGraphUuid, openGraph, setNodes, isCreatingChildGraph]);

    /**
     * Handle double-click submerge gesture
     *
     * Double-clicking a container immediately opens the child graph, providing
     * the same quick-access experience as Articy Draft's submerge gesture.
     *
     * @param {React.MouseEvent} e - Double-click event
     */
    const handleDoubleClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            handleOpenChild();
        },
        [handleOpenChild]
    );

    // Calculate number of output handles based on outputs config
    const outputCount = nodeData.outputs ?? 1;
    const outputHandles = Array.from({ length: outputCount }, (_, i) => i);

    // Determine styling based on state
    const isEmpty = !nodeData.childNodeCount || nodeData.childNodeCount === 0;
    const borderStyle = isEmpty ? 'dashed' : 'solid';

    return (
        <div
            className={`card card-sm container-node ${selected ? 'border-primary shadow-sm' : ''}`}
            style={{
                minWidth: 220,
                minHeight: 100,
                borderStyle,
                borderWidth: '2px',
                opacity: isEmpty ? 0.8 : 1,
            }}
            onDoubleClick={handleDoubleClick}
        >
            {/* Toolbar with Open + Export bundle buttons */}
            <NodeToolbar isVisible={selected} position={Position.Top}>
                <div className="d-flex flex-column gap-1">
                    <div className="d-flex gap-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary nodrag"
                            onClick={handleOpenChild}
                            disabled={isCreatingChildGraph}
                            title="Open child graph (or double-click node)"
                        >
                            {isCreatingChildGraph ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                <>
                                    <i className="ti ti-arrow-up-right me-1" />
                                    Open
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary nodrag"
                            onClick={handleExportBundle}
                            disabled={exportJob?.status === 'pending' || exportJob?.status === 'processing'}
                            title="Export this subtree as a .dgpack bundle"
                        >
                            {exportJob?.status === 'pending' || exportJob?.status === 'processing' ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                <>
                                    <i className="ti ti-package me-1" />
                                    Export
                                </>
                            )}
                        </button>
                    </div>

                    {openError && (
                        <div className="alert alert-danger alert-sm py-1 px-2 mb-0 small nodrag">{openError}</div>
                    )}

                    {exportError && (
                        <div className="alert alert-danger alert-sm py-1 px-2 mb-0 small nodrag">{exportError}</div>
                    )}

                    {exportJob?.status === 'completed' && exportJob.download_url && (
                        <a
                            href={exportJob.download_url}
                            className="btn btn-sm btn-success nodrag"
                            download
                        >
                            Download bundle
                        </a>
                    )}

                    {exportJob?.status === 'failed' && (
                        <div className="alert alert-danger alert-sm py-1 px-2 mb-0 small nodrag">{exportJob.error}</div>
                    )}
                </div>
            </NodeToolbar>

            {/* Color status bar */}
            <div
                className="card-status-top"
                style={{ backgroundColor: nodeData.color ?? '#0d6efd' }}
            />

            {/* Node body */}
            <div className="card-body py-2 px-3">
                {/* Node type indicator */}
                <div className="d-flex align-items-center gap-2 text-muted mb-1">
                    <i className="ti ti-folder" />
                    <span className="text-uppercase small">Container</span>
                </div>

                {/* Label */}
                <div className="small nodrag font-weight-medium">
                    {nodeData.label ?? 'Untitled container'}
                </div>

                {/* Description (if provided) */}
                {nodeData.description && (
                    <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                        {nodeData.description}
                    </div>
                )}

                {/* Child node count badge */}
                <div className="mt-2">
                    <span className="badge bg-secondary-lt">
                        {nodeData.childNodeCount ?? 0} nodes
                    </span>
                </div>

                {/* Empty state indicator */}
                {isEmpty && (
                    <div className="text-muted small mt-2" style={{ fontSize: '0.7rem' }}>
                        Empty • Double-click to create
                    </div>
                )}
            </div>

            {/* Input handle: receives from parent graph */}
            <Handle
                type="target"
                position={Position.Left}
                id="input"
                title="Input from parent graph"
            />

            {/* Output handles: send to parent graph (one per output pin) */}
            {outputHandles.map((i) => (
                <Handle
                    key={`output_${i}`}
                    type="source"
                    position={Position.Right}
                    id={`output_${i}`}
                    style={{
                        top: `${((i + 1) / (outputCount + 1)) * 100}%`,
                    }}
                    title={`Output pin ${i} → Exit ${i} in child graph`}
                />
            ))}
        </div>
    );
};

/**
 * Container Node Metadata
 *
 * Registry entry for the Container node type. Used by the NodeTypeRegistry
 * to register this type with the system and provide UI information.
 *
 * @constant
 * @property {string} type - Unique identifier ('container')
 * @property {string} label - User-facing name in palette
 * @property {string} icon - Emoji for visual identification
 * @property {ContainerNodeData} defaultData - Initial data for new instances
 * @property {string} description - Documentation and tooltip text
 * @property {string} category - Palette grouping
 */
export const containerNodeMeta = {
    type: 'container' as const,
    label: 'Container',
    icon: '📦',
    defaultData: {
        label: 'New container',
        outputs: 1,
        childNodeCount: 0,
    } satisfies ContainerNodeData,
    description: 'A node containing a nested child graph (double-click to open)',
    category: 'nesting',
};
